<template>
  <PullToRefresh
    :onRefresh="handlePullToRefresh"
    :pullText="$t('common.pull_to_refresh')"
    :releaseText="$t('common.release_to_refresh')"
    :refreshingText="$t('common.refreshing')"
  >
  <div class="all-submissions-page">
    <div class="container container--fluid">
      <PageHeader :title="pageTitle" :description="pageDescription">
        <template #actions>
          <div class="all-submissions-page__form-selector">
            <select v-model="selectedFormId" class="form-selector" @change="handleFormChange">
              <option value="">{{ $t('submissions.all_forms') }}</option>
              <option v-for="form in forms" :key="form.id" :value="form.id">
                {{ form.name }}
              </option>
            </select>
          </div>
        </template>
      </PageHeader>

      <!-- Submission Statistics -->
      <SubmissionStats :form-id="selectedFormId" />

      <div v-if="submissions && submissions.length > 0" class="all-submissions-page__content">
        <!-- Use specific form TableView when a form is selected -->
        <TableView
          v-if="selectedFormDetails"
          :key="`table-view-${selectedFormDetails.id}-${selectedStatus}`"
          :submissions="submissions"
          :loading="submissionsLoading"
          :total="submissionPagination.total"
          :current-page="submissionPagination.page"
          :page-size="submissionPagination.limit"
          :initial-status="selectedStatus"
          @page-change="handlePageChange"
          @row-click="openSubmissionModal"
          @status-change="handleStatusChange"
          :form="selectedFormDetails"
          @export="exportSubmissions"
          :is-exporting="isExporting"
        />

        <!-- Use generic AllSubmissions TableView when no specific form is selected -->
        <AllSubmissionsTableView
          v-else
          :key="`all-submissions-${selectedStatus}-${selectedFormIds.join(',')}`"
          :submissions="submissions"
          :loading="submissionsLoading"
          :total="submissionPagination.total"
          :current-page="submissionPagination.page"
          :page-size="submissionPagination.limit"
          :initial-status="selectedStatus"
          :forms="forms"
          :initial-form-ids="selectedFormIds"
          @page-change="handlePageChange"
          @row-click="openSubmissionModal"
          @filter-change="handleFilterChange"
          @export="exportSubmissions"
          :is-exporting="isExporting"
        />
      </div>

      <div v-else-if="!submissionsLoading" class="all-submissions-page__empty">
        <BaseIcon name="inbox" size="48" />
        <h3>{{ $t('submissions.no_submissions') }}</h3>
        <p>{{ $t('submissions.no_submissions_description') }}</p>
      </div>
    </div>
  </div>
  </PullToRefresh>
</template>

<script>
  import {
    defineComponent,
    ref,
    computed,
    onMounted,
    getCurrentInstance,
    watch,
    nextTick,
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useFormStore } from '@/store/formStore';
  import { useUIStore } from '@/store/uiStore';
  import { formService } from '@/services/formService';
  import { formatDate } from '@/utils/date';

  import SubmissionDetails from '@/components/submissions/submissionDetails.vue';
  import SubmissionStats from '@/components/submissions/SubmissionStats.vue';
  import TableView from '@/components/submissions/TableView.vue';
  import AllSubmissionsTableView from '@/components/submissions/AllSubmissionsTableView.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import PullToRefresh from '@/components/partials/PullToRefresh.vue';

  export default defineComponent({
    name: 'AllSubmissionsPage',

    components: {
      SubmissionStats,
      TableView,
      AllSubmissionsTableView,
      PageHeader,
      BaseIcon,
      PullToRefresh,
      LoadingSpinner,
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const route = useRoute();
      const router = useRouter();
      const formStore = useFormStore();
      const uiStore = useUIStore();

      // State
      const selectedFormId = ref('');
      const selectedFormDetails = ref(null);
      const selectedFormIds = ref([]); // For multi-select filter
      const selectedStatus = ref('received'); // Default to 'received' (complete submissions)
      const selectedStatuses = ref(['received']); // For multi-select filter
      const submissions = ref([]);
      const submissionPagination = ref({
        page: 1,
        limit: 50,
        total: 0,
      });
      const submissionsLoading = ref(false);
      const isExporting = ref(false);

      // Computed
      const forms = computed(() => formStore.forms || []);
      const selectedForm = computed(() => {
        if (!selectedFormId.value) return null;
        return forms.value.find((form) => form.id === selectedFormId.value);
      });

      const pageTitle = computed(() => {
        if (selectedForm.value) {
          return `${selectedForm.value.name} - ${$t('submissions.title')}`;
        }
        return $t('submissions.all_submissions');
      });

      const pageDescription = computed(() => {
        if (selectedForm.value) {
          return $t('submissions.view_form_submissions');
        }
        return $t('submissions.view_all_submissions');
      });

      // Methods
      const loadSubmissions = async (page = 1, limit = 50, options = {}) => {
        submissionsLoading.value = true;
        try {
          // Use provided options or fall back to current state
          const statuses = options.statuses || selectedStatuses.value;
          const formIds = options.formIds !== undefined ? options.formIds : selectedFormIds.value;

          // Determine status parameter
          let statusParam = null;
          if (statuses.length === 1) {
            statusParam = statuses[0];
          } else if (statuses.length > 1 && statuses.length < 2) {
            // If not all statuses are selected, pass them as comma-separated
            statusParam = statuses.join(',');
          }
          // If all statuses are selected (or empty), don't filter

          // Determine form parameter
          let formIdParam = selectedFormId.value || null;
          if (formIds.length === 1) {
            formIdParam = formIds[0];
          } else if (formIds.length > 1) {
            // Multiple forms selected - pass as comma-separated
            formIdParam = formIds.join(',');
          }

          const response = await formService.getAllSubmissions({
            formId: formIdParam,
            page,
            limit,
            status: statusParam,
          });

          submissions.value = response.submissions || [];
          submissionPagination.value = {
            page: response.pagination.page || 1,
            limit: response.pagination.limit || 50,
            total: response.pagination.total || 0,
          };
        } catch (error) {
          console.error('Failed to load submissions:', error);
          submissions.value = [];
          submissionPagination.value = { page: 1, limit: 50, total: 0 };
        } finally {
          submissionsLoading.value = false;
        }
      };

      const handlePageChange = async (settings) => {
        // Preserve current filters when paginating
        await loadSubmissions(settings.page, settings.limit, {
          statuses: selectedStatuses.value,
          formIds: selectedFormIds.value,
        });
      };

      const handleStatusChange = async (status) => {
        // Update selectedStatus and reset to page 1
        selectedStatus.value = status;
        selectedStatuses.value = status ? [status] : ['received', 'partial'];
        submissionPagination.value.page = 1;
        // Wait for nextTick to ensure the prop is updated before component remounts
        await nextTick();
        await loadSubmissions(1, submissionPagination.value.limit, {
          statuses: selectedStatuses.value,
          formIds: selectedFormIds.value,
        });
      };

      const handleFilterChange = async ({ statuses, formIds }) => {
        // Update filter state
        selectedStatuses.value = statuses;
        selectedFormIds.value = formIds;
        // Update legacy status for compatibility
        selectedStatus.value = statuses.length === 1 ? statuses[0] : '';
        // Reset pagination
        submissionPagination.value.page = 1;
        await nextTick();
        await loadSubmissions(1, submissionPagination.value.limit, { statuses, formIds });
      };

      // The forms list endpoint only returns summary fields, but TableView
      // needs the full form structure (pages/elements) to build its columns.
      const loadFormDetails = async (formId) => {
        if (!formId) {
          selectedFormDetails.value = null;
          return;
        }
        try {
          const form = await formStore.ensureFormDetails(formId);
          // Guard against out-of-order responses when switching forms quickly
          if (selectedFormId.value === formId) {
            selectedFormDetails.value = form?.pages ? form : null;
          }
        } catch (error) {
          console.error('Failed to load form details:', error);
          if (selectedFormId.value === formId) {
            selectedFormDetails.value = null;
          }
        }
      };

      const handleFormChange = async () => {
        // Reset pagination when changing forms, but keep status
        submissionPagination.value.page = 1;
        // When using the header dropdown, clear multi-select
        selectedFormIds.value = selectedFormId.value ? [selectedFormId.value] : [];
        await Promise.all([
          loadFormDetails(selectedFormId.value),
          loadSubmissions(1, submissionPagination.value.limit, {
            statuses: selectedStatuses.value,
            formIds: selectedFormIds.value,
          }),
        ]);
      };

      // Track currently open submission to prevent double modal
      const currentOpenSubmissionId = ref(null);

      const openSubmissionModal = (submission, updateUrl = true) => {
        // Prevent opening the same submission twice
        if (currentOpenSubmissionId.value === submission?.id) {
          return;
        }

        currentOpenSubmissionId.value = submission?.id;

        // Update URL when opening modal (if not already from URL)
        if (updateUrl && submission?.id) {
          router.push({ name: 'SubmissionDetailDirect', params: { submissionId: submission.id } });
        }

        uiStore.addModal(SubmissionDetails, {
          title: $t('submissions.submission_details'),
          submission,
          size: 'lg',
          form: selectedFormDetails.value || selectedForm.value || submission.form,
          onClose: () => {
            currentOpenSubmissionId.value = null;
            // Reset URL when modal closes
            router.push({ name: 'AllSubmissions' });
          },
        });
      };

      // Load and open a specific submission by ID (for direct URL access)
      const openSubmissionById = async (submissionId) => {
        // Prevent opening the same submission twice
        if (currentOpenSubmissionId.value === submissionId) {
          return;
        }

        try {
          const submission = await formService.getSubmission(submissionId);
          if (submission) {
            openSubmissionModal(submission, false); // Don't update URL again
          }
        } catch (error) {
          console.error('Failed to load submission:', error);
        }
      };

      const exportSubmissions = async () => {
        isExporting.value = true;
        try {
          const response = await formService.exportSubmissions(selectedFormId.value);

          if (!response) throw new Error('Export failed');

          const blob =
            response instanceof Blob ? response : new Blob([response], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');

          const filename = selectedForm.value
            ? `${selectedForm.value.name}-submissions-${formatDate(new Date())}.csv`
            : `all-submissions-${formatDate(new Date())}.csv`;

          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        } catch (error) {
          console.error('Failed to export submissions:', error);
        } finally {
          isExporting.value = false;
        }
      };

      // Pull to refresh handler for mobile
      const handlePullToRefresh = async () => {
        await loadSubmissions(1, submissionPagination.value.limit, {
          statuses: selectedStatuses.value,
          formIds: selectedFormIds.value,
        });
      };

      // Load forms and submissions in parallel for faster initial load
      // fetchForms() has built-in caching - skips API call if already loaded
      onMounted(async () => {
        try {
          await Promise.all([
            formStore.fetchForms(), // Uses cache if forms already loaded
            loadSubmissions(1, 50, {
              statuses: selectedStatuses.value,
              formIds: selectedFormIds.value,
            }),
          ]);

          // Check if there's a submissionId in the URL and open modal
          const submissionId = route.params.submissionId;
          if (submissionId) {
            await openSubmissionById(submissionId);
          }
        } catch (error) {
          console.error('Failed to initialize submissions page:', error);
        }
      });

      // Watch for route changes to handle direct navigation to submission
      watch(
        () => route.params.submissionId,
        async (newSubmissionId) => {
          if (newSubmissionId) {
            await openSubmissionById(newSubmissionId);
          }
        }
      );

      // Watch for form changes
      watch(selectedFormId, () => {
        handleFormChange();
      });

      return {
        selectedFormId,
        selectedFormIds,
        selectedStatus,
        submissions,
        submissionPagination,
        submissionsLoading,
        isExporting,
        forms,
        selectedForm,
        selectedFormDetails,
        pageTitle,
        pageDescription,
        handlePageChange,
        handleStatusChange,
        handleFilterChange,
        handleFormChange,
        openSubmissionModal,
        exportSubmissions,
        handlePullToRefresh,
      };
    },
  });
</script>

<style scoped lang="scss">
  .all-submissions-page {
    min-height: 100vh;
    background: var(--slate-50);

    &__form-selector {
      .form-selector {
        padding: 0.5rem 0.75rem;
        border: 1px solid var(--slate-300);
        border-radius: 0.375rem;
        background: white;
        font-size: 0.875rem;
        color: var(--slate-700);
        min-width: 200px;

        &:focus {
          outline: none;
          border-color: var(--brand-500);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
      }
    }

    &__content {
      margin-top: 1rem;
    }

    &__empty {
      text-align: center;
      padding: 3rem 1rem;
      color: var(--slate-500);

      .icon {
        color: var(--slate-400);
        margin-bottom: 1rem;
      }

      h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--slate-900);
      }

      p {
        margin: 0;
        font-size: 0.875rem;
      }
    }
  }
</style>
